import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, X } from "lucide-react";
import { toast } from "@/lib/appToast";
import {
  formatCardapioNotificationBody,
  formatCardapioNotificationTitle,
  type CardapioCaixaNotification,
} from "@/lib/cardapioOperationalNotifications";
import {
  encerrarCardapioCaixaNotificacao,
  listarCardapioCaixaNotificacoes,
} from "@caixa/lib/cardapioNotificationsApi";

const POLL_MS = 3000;

export function CardapioCaixaNotifications() {
  const [notificacoes, setNotificacoes] = useState<CardapioCaixaNotification[]>([]);
  const [dismissingIds, setDismissingIds] = useState<Set<number>>(() => new Set());
  const pollingRef = useRef(false);

  const carregar = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const lista = await listarCardapioCaixaNotificacoes();
      setNotificacoes(lista.filter((n) => n.tipo !== "waiter_request"));
    } catch {
      /* mantém lista atual em falhas transitórias de rede */
    } finally {
      pollingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let intervalId: number | null = null;

    const poll = () => {
      if (document.hidden) return;
      void carregar();
    };

    poll();
    intervalId = window.setInterval(poll, POLL_MS);

    const onVisibility = () => {
      if (!document.hidden) void carregar();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (intervalId != null) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [carregar]);

  const handleDismiss = async (id: number) => {
    if (dismissingIds.has(id)) return;
    setDismissingIds((prev) => new Set(prev).add(id));
    try {
      await encerrarCardapioCaixaNotificacao(id);
      setNotificacoes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao encerrar notificação.");
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (notificacoes.length === 0 || typeof document === "undefined") return null;

  return createPortal(
    <div className="cardapio-caixa-notifications-portal" aria-live="polite">
      {notificacoes.map((notification) => (
        <div key={notification.id} className="cardapio-caixa-notification" role="status">
          <div className="cardapio-caixa-notification-icon" aria-hidden>
            <Bell className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="cardapio-caixa-notification-content">
            <p className="cardapio-caixa-notification-title">
              {formatCardapioNotificationTitle(notification.tipo)}
            </p>
            <p className="cardapio-caixa-notification-body">{formatCardapioNotificationBody(notification)}</p>
          </div>
          <button
            type="button"
            className="cardapio-caixa-notification-close"
            aria-label="Fechar notificação"
            disabled={dismissingIds.has(notification.id)}
            onClick={() => void handleDismiss(notification.id)}
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
